
<template>
  <div class="container" v-if="!inquiryTried">
    <!-- 👇 조회 폼 -->
    <h2 class="page-title">청약 조회</h2>
    <p class="subtitle">청약신청시 기입한 정보를 입력해주세요.</p>
    <div class="required-note">
      <span class="required">*</span> 표기 항목은 필수항목입니다.
    </div>

    <div class="form-group">
      <div class="form-row">
        <label for="name">이름 <span class="required">*</span></label>
        <input type="text" id="name" v-model="inquiryForm.name" required />
      </div>
      <div class="form-row">
        <label for="phone">연락처 <span class="required">*</span></label>
        <div class="phone-input-group">
          <input type="text" id="phone1" v-model="phoneInputs.first" maxlength="3" @input="formatPhoneNumber" @keyup="autoMoveNext($event, 'phone2')" />
          <span class="phone-separator">-</span>
          <input type="text" id="phone2" v-model="phoneInputs.middle" maxlength="4" @input="formatPhoneNumber" @keyup="autoMoveNext($event, 'phone3')" />
          <span class="phone-separator">-</span>
          <input type="text" id="phone3" v-model="phoneInputs.last" maxlength="4" @input="formatPhoneNumber" />
        </div>
      </div>
    </div>
    <div class="button-group">
      <button class="btn-submit" @click="submitInquiry">조회하기</button>
    </div>
  </div>

  <!-- 👇 조회 결과 테이블 -->
  <div v-if="inquiryTried" class="inquiry-result">
    <h3>청약 신청 내역</h3>
    <p class="muted small" style="margin:6px 0 12px;">
      ※ 현재 공고(있을 경우)와 <strong>공고일 기준 최근 6개월 이내</strong> 신청 내역만 표시됩니다.
    </p>

    <table class="inquiry-table">
      <thead>
        <tr>
          <th>공고일</th>
          <th>신청일</th>
          <th>이름</th>
          <th>연락처</th>
          <th>신청 유형</th>
          <th>진행상황</th>
          <th>관리</th>
        </tr>
      </thead>

      <tbody>
        <!-- ✅ 1) 가장 최근 공고 1행 (기존 동작) -->
        <tr v-if="inquiryResult">
          <td>{{ formatDate(inquiryResult.noticeAt) }}</td>
          <td>{{ formatDate(inquiryResult.createdAt) }}</td>
          <td>{{ inquiryResult.name }}</td>
          <td>{{ formatPhoneDisplay(inquiryResult.phoneNumber) }}</td>
          <td>{{ mapSupplyTypeLabel(inquiryResult.subscriptionType) }}</td>
          <td :class="isSubscriptionClosed ? winningStateClass : ''">
            <span>{{ progressStatusText }}</span>
          </td>
          <td>
            <div v-if="subscriptionPhase === 'open'" class="action-btns">
              <button class="edit-btn" @click="goToEditPage">수정하기</button>
              <button class="delete-btn" @click="deleteApplication">접수취소</button>
            </div>
            <div v-else-if="subscriptionPhase === 'pending'" class="muted">-</div>
            <div v-else class="muted">-</div>
          </td>
                    <!-- <td>
            <div class="action-btns">
              <button class="edit-btn"
                      @click="goToEditPage"
                      :disabled="subscriptionPhase !== 'open'">
                수정하기
              </button>
              <button class="delete-btn"
                      @click="deleteApplication"
                      :disabled="subscriptionPhase !== 'open'">
                접수취소
              </button>
            </div>
          </td> -->

        </tr>

        <!-- [NEW] ✅ 2) (가장 최근 제외) 최근 3개월 내 공고에 대한 내 신청 행들 -->
        <tr v-for="row in past3mRows" :key="row.applicant.id">
          <td>{{ formatDate(row.applicant.noticeAt) }}</td>
          <td>{{ formatDate(row.applicant.createdAt) }}</td>
          <td>{{ row.applicant.name || inquiryForm.name }}</td>
          <td>{{ formatPhoneDisplay(row.applicant.phoneNumber || inquiryForm.phone) }}</td>
          <td>{{ mapSupplyTypeLabel(row.applicant.subscriptionType) }}</td>
          <td :class="row.closed ? winningClass(row.applicant?.winningState) : ''">
            {{ row.closed ? (row.applicant?.winningState || '미당첨') : '청약신청완료' }}
          </td>
          <!-- 관리 열은 '현재가 청약신청완료'일 때만 존재하므로 과거행은 '-' 처리 -->
          <td>
            <div v-if="!row.closed && subscriptionPhase === 'open'" class="action-btns">
              <button class="edit-btn" @click="goEdit(row.applicant.id)">수정하기</button>
              <button class="delete-btn" @click="deleteById(row.applicant.id)">접수취소</button>
            </div>
            <div v-else class="muted">-</div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- [NEW] 로딩 표시(선택) -->
    <div v-if="pastLoading" class="muted" style="margin-top:8px;">과거 6개월 내 기록 불러오는 중…</div>
    <div v-else-if="!inquiryResult && past3mRows.length === 0" class="muted" style="margin-top:8px;">
      조회된 청약 신청 내역이 없습니다.
    </div>  <!-- [NEW] -->
  </div>
</template>



<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import axios from '@/utils/axios';
import router from '@/router';

const inquiryForm = ref({ name: '', phone: '' });

// 입력 쪼개기
const phoneInputs = ref({ first: '', middle: '', last: '' });
watch(phoneInputs, (v) => {
  inquiryForm.value.phone = `${v.first}${v.middle}${v.last}`;
}, { deep: true });

// 🔥 상태
const currentEndTs = ref(null);
const subscriptionList = ref([]);
const subscriptionStatusText = ref("");
const inquiryResult = ref(null);
const inquiryTried = ref(false);
const isSubscriptionClosed = ref(false);

// [NEW] ✅ 현재(가장 최근) 공고 id 저장
const currentSubscriptionId = ref(null);

// [NEW] ✅ (가장 최근 제외) 최근 3개월 내 행들
const past3mRows = ref([]);
// [NEW] ✅ 로딩 표시
const pastLoading = ref(false);

// 새 computed: 접수 상태를 4단계로 구분
const subscriptionPhase = computed(() => {
  // current subscription 기준
  const sub = subscriptionList.value?.find(s => String(s.id) === String(currentSubscriptionId.value));
  if (!sub) return 'none';

  const now  = new Date();
  const st   = new Date(sub.startTs);
  const end  = new Date(sub.endTs);
  const done = !!sub.closed;

  if (done) return 'closed';
  if (now < st) return 'before';
  if (now >= st && now < end) return 'open';
  // endTs 지난 후 closed 전까지
  return 'pending';
});

// 표시용 텍스트: 기존 progressStatusText 대체 또는 내부에서 사용
const progressStatusText = computed(() => {
  switch (subscriptionPhase.value) {
    case 'before':  return '예정';
    case 'open':    return '청약신청완료';
    case 'pending': return '발표대기';
    case 'closed':  return inquiryResult.value?.winningState || '미당첨';
    default:        return '';
  }
});

// [NEW] ✅ 당첨 결과 클래스 공용
const winningClass = (state) => {
  if (!state) return '';
  if (state === '당첨') return 'text-blue';
  if (state === '미당첨') return 'text-red';
  if (state.startsWith('예비')) return 'text-green';
  return '';
};

const fetchSubscriptionStatus = async () => {
  const subscriptionRes = await axios.get("/bx201/subscription");
  const list = subscriptionRes.data || [];
  subscriptionList.value = list;

  // endTs 최신순 → 0번 최신
  let subscription = list[0];

  const now = new Date();
  // 시작 전이면 전꺼(1번)
  if (subscription && new Date(subscription.startTs) > now && list.length > 1) {
    subscription = list[1];
  }

  // [NEW] 현재 공고 id 기록
  currentSubscriptionId.value = subscription ? subscription.id : null;

  if (subscription) {
    const end = new Date(subscription.endTs);
    if (now < end) subscriptionStatusText.value = "청약신청완료";
    else if (now >= end && !subscription.closed) subscriptionStatusText.value = "청약신청완료";
    else if (subscription.closed) subscriptionStatusText.value = "청약신청완료";
    isSubscriptionClosed.value = !!subscription.closed;
  } else {
    subscriptionStatusText.value = "";
    isSubscriptionClosed.value = false;
  }
};

// const progressStatusText = computed(() => {
//   if (isSubscriptionClosed.value) return inquiryResult.value?.winningState || "미당첨";
//   return subscriptionStatusText.value;
// });

// [CHANGED] 공용 winningClass 사용
const winningStateClass = computed(() => {
  if (!inquiryResult.value?.winningState) return '';
  return winningClass(inquiryResult.value.winningState);
});

// 숫자만
const formatPhoneNumber = () => {
  phoneInputs.value.first  = phoneInputs.value.first.replace(/[^0-9]/g, '');
  phoneInputs.value.middle = phoneInputs.value.middle.replace(/[^0-9]/g, '');
  phoneInputs.value.last   = phoneInputs.value.last.replace(/[^0-9]/g, '');
};

const autoMoveNext = (e, nextId) => {
  const input = e.target;
  if (input.value.length >= input.maxLength) document.getElementById(nextId)?.focus();
};

const formatPhoneDisplay = (phone) => {
  if (!phone) return "정보 없음";
  const digits = phone.replace(/[^0-9]/g, '');
  if (digits.length === 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11) return `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`;
  return phone;
};

// [NEW] 6개월 전 시각
const threeMonthsAgo = () => {
  const now = new Date();
  const past = new Date(now);
  past.setMonth(now.getMonth() - 6);
  return past;
};

// [NEW] (가장 최근 제외) 최근 3개월 내 공고 uid로 개별 조회 → 같은 테이블에 row 추가
const fetchPast3mRowsByUid = async () => {
  past3mRows.value = [];
  pastLoading.value = true;
  try {
    // 사전조건: subscriptionList 준비됨
    if (!subscriptionList.value?.length) {
      const res = await axios.get("/bx201/subscription");
      subscriptionList.value = res.data || [];
    }

    const since = threeMonthsAgo();
    const now = new Date();

    // (가장 최근 제외) 최근 3개월(종료일 기준, 미래 제외)의 공고들
    const targetSubs = subscriptionList.value
      .filter(sub => {
        if (currentSubscriptionId.value && String(sub.id) === String(currentSubscriptionId.value)) return false;
        const end = new Date(sub.noticeAt);
        return end >= since && end <= now;
      })
      .sort((a, b) => new Date(b.noticeAt) - new Date(a.noticeAt)); // 최신 종료일 우선

    // 순차 조회 (동시 진행 없음 가정, 과도한 트래픽 방지)
    for (const sub of targetSubs) {
      try {
        const r = await axios.get("/user/subscriptionApplicants", {
          params: {
            name: inquiryForm.value.name,
            phone: inquiryForm.value.phone,
            uid: sub.id, // ★ 서버 스펙: uid 있으면 해당 공고 단건 조회
          },
        });
        const applicant = r.data;
        if (applicant) {
          const closed = !!sub.closed || new Date(sub.endTs) < now;
          past3mRows.value.push({ applicant, subscription: sub, closed });
        }
      } catch (e) {
        // 404: 해당 공고에 신청 없음 → 스킵
        if (e?.response?.status !== 404) {
          // 기타 오류는 콘솔만
          console.warn('past 3m fetch error on uid=', sub.id, e);
        }
      }
    }
  } finally {
    pastLoading.value = false;
  }
};

const submitInquiry = async () => {
  if (!inquiryForm.value.name || !inquiryForm.value.phone) {
    alert("이름, 연락처를 모두 입력해 주세요.");
    return;
  }

  try {
    await fetchSubscriptionStatus();   // 현재 공고 id, 진행상태 세팅

    // 현재/직전 공고 1건 (uid 없이)
    let current = null;
    try {
      const res = await axios.get("/user/subscriptionApplicants", {
        params: { name: inquiryForm.value.name, phone: inquiryForm.value.phone },
      });
      current = res.data;
    } catch (e) {
      // 현재 공고에 신청이 없으면 404가 정상 → 무시
      if (e?.response?.status !== 404) throw e;
    }

    inquiryResult.value = current || null;

    // endTs 매칭
    if (inquiryResult.value && subscriptionList.value.length > 0) {
      const match = subscriptionList.value.find(
        sub => String(sub.id) === String(inquiryResult.value.subscriptionUid)
      );
      currentEndTs.value = match?.endTs ?? null;
    } else {
      currentEndTs.value = null;
    }

    // (가장 최근 제외) 최근 3개월 uid 개별 조회 → 같은 테이블에 추가 행
    await fetchPast3mRowsByUid();

    // 결과 섹션 보이기
    inquiryTried.value = true;
  } catch (err) {
    alert("조회 중 오류가 발생했습니다.");
    console.error(err);
    currentEndTs.value = null;

    // 실패했어도 최근 3개월은 시도
    try {
      await fetchSubscriptionStatus();
      await fetchPast3mRowsByUid();
    } finally {
      inquiryTried.value = true;
    }
  }
};

const mapSupplyTypeLabel = (type) => {
  switch (type) {
    case 'GENERAL-15': return '15평 청년 일반공급';
    case 'SPECIAL-15': return '15평 청년 특별공급';
    case 'GENERAL-30': return '30평 청년 & 신혼부부 일반공급';
    default: return type;
  }
};

const formatDate = (ds) => {
  if (!ds) return "정보 없음";
  const d = new Date(ds);
  return d.toLocaleDateString();
};

const goEdit = (id) => {
  if (!id) return;
  router.push({ name: 'ApplicationEdit', params: { id } });
};

const deleteById = async (id) => {
  if (!id) return;
  if (!confirm('정말 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.')) return;
  try {
    await axios.delete(`/user/subscriptionApplicants/${id}`);
    alert('신청 내역이 삭제되었습니다.');
    // 테이블 재빌드
    await fetchPast3mRowsByUid();
    if (inquiryResult.value?.id === id) inquiryResult.value = null;
  } catch (e) {
    alert('삭제 중 오류가 발생했습니다.');
  }
};

const goToEditPage = () => {
  if (!inquiryResult.value?.id) return;
  router.push({ name: 'ApplicationEdit', params: { id: inquiryResult.value.id } });
};

const deleteApplication = async () => {
  if (!inquiryResult.value?.id) return;
  if (!confirm("정말 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.") ) return;

  try {
    await axios.delete(`/user/subscriptionApplicants/${inquiryResult.value.id}`);
    alert('신청 내역이 삭제되었습니다.');
    router.push('/apply');
  } catch (e) {
    alert('삭제 중 오류가 발생했습니다.');
  }
};

onMounted(fetchSubscriptionStatus);
</script>

<style scoped>
.inquiry-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 100px;
}
.inquiry-table th,
.inquiry-table td {
  border: 1px solid #e1e1e1;
  padding: 10px 16px;
  /* text-align: left; */
  background: #fff;
}

.inquiry-table th {
  background: #f7f7f7;
  width: 140px;
}

/* 전체 표 가운데 정렬 */
.inquiry-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 100px;
  text-align: center;              /* ✅ 핵심: 모든 셀 중앙 정렬 */
}

.inquiry-table th,
.inquiry-table td {
  border: 1px solid #e1e1e1;
  padding: 10px 16px;
  background: #fff;
  vertical-align: middle;          /* 세로 가운데 */
}

.inquiry-table th {
  background: #f7f7f7;
  width: auto;                     /* 필요 시 조절 */
}

/* 액션 버튼 컨테이너: 가운데 정렬 */
.action-btns {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;         /* ✅ 가운데 */
}

.container {
  max-width: 600px;
  margin: 40px auto;
  padding: 20px;
  font-family: 'Noto Sans KR', sans-serif;
  
}

.page-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  text-align: center;
}

.subtitle {
  text-align: center;
  color: #555;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  margin-bottom: 20px;
  position: relative;
}

.form-row label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-row input {
  width: 100%;
  padding: 10px 0;
  border: none;
  border-bottom: 1px solid #ddd;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s;
}

.form-row input:focus {
  border-bottom-color: #003478;
}

.phone-input-group {
  display: flex;
  align-items: center;
}

.phone-input-group input {
  width: 33%;
  text-align: center;
}

.phone-separator {
  margin: 0 8px;
  color: #666;
}

.required {
  color: #ff0000;
}

.required-note {
  font-size: 12px;
  color: #666;
  text-align: right;
  margin-bottom: 30px;
}

.button-group {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  margin-bottom: 30px;
}

.btn-submit {
  padding: 12px 40px;
  background-color: #003478;
  color: white;
  border: none;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-submit:hover {
  background-color: #002255;
}

.inquiry-result {
  max-width: 1200px;
  margin: 30px auto 0 auto;   /* 위 30px, 좌우 자동, 아래 0 */
  padding: 50px;
  padding-bottom: 100px;
  border-radius: 4px;
  text-align: center;         /* 모든 자식(표, 텍스트, h3 등) 가운데 정렬 */
}

.inquiry-result h3 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 18px;
}

.inquiry-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.inquiry-list li {
  margin-bottom: 10px;
}

.subscription-status {
  margin-top: 20px;
  text-align: center;
  font-size: 18px;
}

.subscription-result {
  display: block;
  margin-top: 10px;
  font-size: 22px;
  font-weight: bold;
}

.text-blue {
  color: #007bff;
}

.text-red {
  color: #e74c3c;
}

.text-green {
  color: #28a745;
}

.status-message {
  margin: 10px 0 24px 0;
  text-align: center;
  font-weight: 600;
  color: #195b8a;
  font-size: 1.06rem;
}
/* td 내 버튼 가로 정렬용 */
.action-btns {
  display: flex;
  font-size: 8px;
  gap: 7px;      /* 버튼 사이 간격 */
  /* justify-content: flex-start; 왼쪽 정렬, 필요시 center/right */
  align-items: center;
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .inquiry-table th,
  .inquiry-table td {
    font-size: 0.90rem;   /* 폰트 더 작게 */
    padding: 8px 4px;     /* 패딩도 더 작게 */
    max-width: 80px;      /* 모바일에서 더 줄임 */
  }
  .inquiry-result {
    padding: 16px;
  }
  .page-title {
    font-size: 1.15rem;
  }
  .subtitle {
    font-size: 0.97rem;
  }
}
.edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #007bff;
  border: 1px solid #007bff;
  border-radius: 25px;
  padding: 8px 20px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 10px;
  cursor: pointer;
  transition: background 0.18s, color 0.18s, border-color 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 7px 0 rgba(231, 76, 60, 0.07);
}


.edit-btn:hover {
  background: #e3f0ff;
  color: #0056b3;
  border-color: #0056b3;
}

/* 버튼 스타일 크게, 둥글게, 그림자 추가 */
.delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #e74c3c;
  border: 1.5px solid #e74c3c;
  border-radius: 25px;
  padding: 8px 20px;
  font-size: 10px;
  font-weight: 600;
  margin-left: 0;
  cursor: pointer;
  transition: background 0.18s, color 0.18s, border-color 0.18s, box-shadow 0.18s;
  box-shadow: 0 1px 7px 0 rgba(231, 76, 60, 0.07);
}

.delete-btn:hover {
  background: #ffeaea;
  color: #fff;
  border-color: #e74c3c;
  box-shadow: 0 2px 12px 0 rgba(231, 76, 60, 0.13);
}

.action-btns button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>